import React from 'react';
import { Shield, AlertTriangle, Search, Filter, RefreshCw, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { LoadingOverlay } from '../../components/ui/LoadingOverlay';
import { useLoading } from '../../hooks/useLoading';
import { sleep } from '../../utils/sleep';
import { cveMetrics, recentCVEs, affectedAssets, cvesByCategory } from '../../data/cve';

const CVEDashboard = () => {
  const { isLoading, withLoading } = useLoading();

  const handleRefresh = async () => {
    await withLoading(async () => {
      await sleep(3000);
      console.log('Refreshing CVE data...');
    });
  };

  return (
    <div className="space-y-6 relative">
      {isLoading && <LoadingOverlay message="Refreshing CVE data..." />}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Shield className="h-8 w-8 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold text-white">CVE Dashboard</h1>
            <p className="text-sm text-gray-400">Monitor and track Common Vulnerabilities and Exposures</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Button variant="outline" onClick={handleRefresh}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button>
            <Search className="h-4 w-4 mr-2" />
            Search CVEs
          </Button>
        </div>
      </div>

      {/* CVE Overview */}
      <div className="grid grid-cols-4 gap-6">
        {cveMetrics.map((metric) => {
          const Icon = metric.icon;
          return (
            <div key={metric.label} className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">{metric.label}</span>
                <Icon className={`h-5 w-5 ${metric.color}`} />
              </div>
              <div className="flex items-end justify-between">
                <div className="text-2xl font-bold text-white">{metric.value}</div>
                <div className={`text-xs ${metric.trend.startsWith('+') ? 'text-red-500' : 'text-emerald-500'}`}>
                  {metric.trend}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent CVEs and Affected Assets */}
      <div className="grid grid-cols-2 gap-6">
        {/* Recent CVEs */}
        <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Recent CVEs</h2>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm">
                <Filter className="h-4 w-4 mr-2" />
                Filter
              </Button>
            </div>
          </div>
          <div className="space-y-4">
            {recentCVEs.map((cve) => (
              <div key={cve.id} className="p-4 bg-gray-800/30 rounded-lg">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        cve.severity === 'Critical' ? 'bg-red-500/10 text-red-500' :
                        cve.severity === 'High' ? 'bg-orange-500/10 text-orange-500' :
                        'bg-yellow-500/10 text-yellow-500'
                      }`}>
                        {cve.severity}
                      </span>
                      <span className="text-sm font-medium text-white">{cve.id}</span>
                    </div>
                    <p className="text-sm text-gray-400 mt-2">{cve.description}</p>
                  </div>
                  <Button variant="outline" size="sm">
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex items-center space-x-4 mt-3 text-xs text-gray-500">
                  <span>Published: {cve.published}</span>
                  <span>CVSS: {cve.cvss}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Affected Assets */}
        <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Affected Assets</h2>
            <Button variant="outline" size="sm">View All</Button>
          </div>
          <div className="space-y-4">
            {affectedAssets.map((asset) => (
              <div key={asset.name} className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg">
                <div>
                  <div className="text-sm font-medium text-white">{asset.name}</div>
                  <div className="text-xs text-gray-400 mt-1">
                    {asset.vulnerableCVEs} vulnerable CVEs
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className={`px-2 py-1 text-xs rounded-full ${
                    asset.riskLevel === 'High' ? 'bg-red-500/10 text-red-500' :
                    asset.riskLevel === 'Medium' ? 'bg-yellow-500/10 text-yellow-500' :
                    'bg-emerald-500/10 text-emerald-500'
                  }`}>
                    {asset.riskLevel} Risk
                  </div>
                  <Button variant="outline" size="sm">Details</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CVE Categories */}
      <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
        <h2 className="text-lg font-semibold text-white mb-4">CVE Categories</h2>
        <div className="grid grid-cols-3 gap-6">
          {cvesByCategory.map((category) => (
            <div key={category.name} className="p-4 bg-gray-800/30 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-white">{category.name}</span>
                <AlertTriangle className={`h-4 w-4 ${category.color}`} />
              </div>
              <div className="text-2xl font-bold text-white mb-2">{category.count}</div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">{category.percentage}% of total</span>
                <span className={`text-xs ${category.trend.startsWith('+') ? 'text-red-500' : 'text-emerald-500'}`}>
                  {category.trend}
                </span>
              </div>
              <div className="mt-3 h-1 bg-gray-800 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${category.color}`}
                  style={{ width: `${category.percentage}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CVEDashboard;